/*
Month view of the releases calendar: the chosen month in weeks from Monday, with the tail
of the neighbouring months dimmed. A day shows up to CHIPS_PER_DAY models of the chosen
type (logo + name), the rest behind "+N more"; today is marked. On a narrow screen the
same days become a list: only days with releases, each headed by its weekday.
*/
import { dateFromKey, dateKey, element } from './eventsData';
import { monthKey, todayKey } from './releasesData';
import { modelChip } from './releasesView';

const CHIPS_PER_DAY = 3;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * @param {{studios: Array, releases: Array}} data calendar from the radio
 * @param {{type: string, month: string}} state chosen type and month (YYYY-MM)
 * @param {Date} now
 * @returns {HTMLElement}
 */
export function buildMonth(data, state, now) {
    const studios = new Map(data.studios.map((studio) => [studio.id, studio]));
    const byDay = new Map();
    data.releases.forEach((release) => {
        if (release.type === state.type && studios.has(release.studio)) {
            byDay.set(release.date, [...(byDay.get(release.date) || []), release]);
        }
    });
    const grid = element('div', 'th-rel-month');
    const head = element('div', 'th-rel-month__weekdays');
    head.setAttribute('aria-hidden', 'true');
    WEEKDAYS.forEach((name) => head.append(element('span', '', name)));
    const days = element('ol', 'th-rel-month__days');
    monthDays(state.month).forEach((day) => {
        days.append(dayCell(day, { byDay, studios, month: state.month, today: todayKey(now) }));
    });
    grid.append(head, days);
    return grid;
}

/** Local days of full weeks (Monday to Sunday) covering the month. */
function monthDays(month) {
    const first = dateFromKey(`${month}-01`);
    const start = new Date(first);
    start.setDate(1 - ((first.getDay() + 6) % 7));
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    const end = new Date(last);
    end.setDate(last.getDate() + (6 - ((last.getDay() + 6) % 7)));
    const days = [];
    for (const day = new Date(start); day <= end; day.setDate(day.getDate() + 1)) {
        days.push(new Date(day));
    }
    return days;
}

function dayCell(day, context) {
    const key = dateKey(day);
    const releases = context.byDay.get(key) || [];
    const cell = element('li', 'th-rel-month__day');
    cell.dataset.outside = String(monthKey(key) !== context.month);
    cell.dataset.empty = String(releases.length === 0);
    cell.append(dayHead(day, key, context));
    releases.slice(0, CHIPS_PER_DAY).forEach((release) => {
        cell.append(modelChip(release, context.studios.get(release.studio)));
    });
    if (releases.length > CHIPS_PER_DAY) {
        const more = element('button', 'th-rel-more', `+${releases.length - CHIPS_PER_DAY} more`);
        more.type = 'button';
        more.dataset.pop = `day:${key}`;
        cell.append(more);
    }
    return cell;
}

/** The day number; the weekday before it is shown only in the narrow-screen list. */
function dayHead(day, key, context) {
    const head = element('p', 'th-rel-month__head');
    const label = key === context.today ? `${day.getDate()}` : dayLabel(day, context.month);
    head.append(element('span', 'th-rel-month__weekday', listWeekday(day, context.month)));
    const date = element('span', 'th-rel-month__date', label);
    date.dataset.today = String(key === context.today);
    head.append(date);
    return head;
}

/** "Mon", or "Mon Aug" for a day of a neighbouring month. */
function listWeekday(day, month) {
    const weekday = WEEKDAYS[(day.getDay() + 6) % 7];
    if (monthKey(dateKey(day)) === month) {
        return weekday;
    }
    return `${weekday} ${shortMonth(day)}`;
}

function shortMonth(day) {
    return day.toLocaleDateString('en-US', { month: 'short' });
}

/** "1", or "Oct 1" on the first day of a neighbouring month. */
function dayLabel(day, month) {
    const outside = monthKey(dateKey(day)) !== month;
    if (outside && day.getDate() === 1) {
        return `${shortMonth(day)} 1`;
    }
    return String(day.getDate());
}
